<template>
  <AppLayout>
    <div class="fleet-page">
     

      <section class="metric-grid">
        <article class="metric-card">
          <p>Total Vehicles</p>
          <h3>{{ stats.totalVehicles }}</h3>
        </article>
        <article class="metric-card">
          <p>Active Trips</p>
          <h3>{{ stats.activeTrips }}</h3>
        </article>
        <article class="metric-card">
          <p>Total Drivers</p>
          <h3>{{ stats.totalDrivers }}</h3>
        </article>
        <article class="metric-card">
          <p>Pending Fuel Requests</p>
          <h3>{{ stats.pendingFuelRequests }}</h3>
        </article>
        <article class="metric-card warning">
          <p>Expiring Compliance</p>
          <h3>{{ stats.expiringCompliance }}</h3>
        </article>
      </section>

      <section class="charts-grid">
        <div class="chart-card">
          <h4 class="chart-title">Vehicles by Status</h4>
          <div class="chart-body">
            <Doughnut
              v-if="vehicleStatusChartData && Array.isArray(vehicleStatusChartData.datasets) && vehicleStatusChartData.datasets.length > 0"
              :data="vehicleStatusChartData"
              :options="doughnutOptions"
            />
            <p v-else class="chart-empty">No vehicle status data yet.</p>
          </div>
        </div>
        <div class="chart-card">
          <h4 class="chart-title">Fuel Requests by Status</h4>
          <div class="chart-body">
            <Bar
              v-if="fuelStatusChartData && Array.isArray(fuelStatusChartData.datasets) && fuelStatusChartData.datasets.length > 0"
              :data="fuelStatusChartData"
              :options="barOptions"
            />
            <p v-else class="chart-empty">No fuel request data yet.</p>
          </div>
        </div>
        <div class="chart-card">
          <h4 class="chart-title">Trip Activity — Last 7 Days</h4>
          <div class="chart-body">
            <Bar
              v-if="tripActivityChartData && Array.isArray(tripActivityChartData.datasets) && tripActivityChartData.datasets.length > 0"
              :data="tripActivityChartData"
              :options="barOptions"
            />
            <p v-else class="chart-empty">No trip activity yet.</p>
          </div>
        </div>
        <div class="chart-card">
          <h4 class="chart-title">Compliance Risk Overview</h4>
          <div class="chart-body">
            <Doughnut
              v-if="complianceStatusChartData && Array.isArray(complianceStatusChartData.datasets) && complianceStatusChartData.datasets.length > 0"
              :data="complianceStatusChartData"
              :options="doughnutOptions"
            />
            <p v-else class="chart-empty">No compliance data yet.</p>
          </div>
        </div>
      </section>

      




    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue';
import fleetApi from '@/api/fleet';
import { Bar, Doughnut } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
} from 'chart.js';

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement);

export default {
  name: 'FleetDashboard',
  components: { AppLayout, Bar, Doughnut },
  data() {
    return {
      loading: false,
      tabs: ['Vehicles', 'Drivers', 'Trips', 'Fuel', 'Compliance'],
      activeTab: 'Vehicles',
      vehicles: [],
      drivers: [],
      trips: [],
      fuels: [],
      compliances: [],
      stats: {
        totalVehicles: 0,
        activeTrips: 0,
        totalDrivers: 0,
        pendingFuelRequests: 0,
        expiringCompliance: 0,
      },
      emptyChartData: {
        labels: [],
        datasets: [],
      },
      doughnutOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
        },
      },
      barOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          y: { beginAtZero: true, ticks: { stepSize: 1, precision: 0 } },
        },
      },
    };
  },
  computed: {
    activeHeaders() {
      if (this.activeTab === 'Vehicles') return ['Plate', 'Type', 'Fuel Type', 'Status', 'Odometer'];
      if (this.activeTab === 'Drivers') return ['License Number', 'License Expiry', 'Status'];
      if (this.activeTab === 'Trips') return ['Vehicle', 'Origin', 'Destination', 'Start Time', 'End Time'];
      if (this.activeTab === 'Fuel') return ['Project', 'Vehicle', 'Requester', 'Status', 'Total Cost'];
      return ['Vehicle', 'Document Type', 'Expiry Date', 'Status'];
    },
    activeRows() {
      if (this.activeTab === 'Vehicles') {
        return this.vehicles.map((v) => ({
          _key: `v-${v.id || v.plateNumber}`,
          cells: [
            { key: 'plate', value: v.plateNumber || '-' },
            { key: 'type', value: v?.type?.name || '-' },
            { key: 'fuelType', value: v?.fuelType?.name || '-' },
            { key: 'status', value: v?.status?.name || '-' },
            { key: 'odometer', value: this.formatNumber(v.currentOdometer) },
          ],
        }));
      }

      if (this.activeTab === 'Drivers') {
        return this.drivers.map((d) => ({
          _key: `d-${d.id || d.licenseNumber}`,
          cells: [
            { key: 'license', value: d.licenseNumber || '-' },
            { key: 'expiry', value: this.formatDate(d.licenseExpiry) },
            { key: 'status', value: d.licenseStatus || 'VALID' },
          ],
        }));
      }

      if (this.activeTab === 'Trips') {
        return this.trips.map((t) => ({
          _key: `t-${t.id || t.startTime}`,
          cells: [
            { key: 'vehicle', value: t?.vehicle?.plateNumber || '-' },
            { key: 'origin', value: t?.requisition?.origin || '-' },
            { key: 'destination', value: t?.requisition?.destination || '-' },
            { key: 'start', value: this.formatDate(t.startTime) },
            { key: 'end', value: t.endTime ? this.formatDate(t.endTime) : 'In Progress' },
          ],
        }));
      }

      if (this.activeTab === 'Fuel') {
        return this.fuels.map((f) => ({
          _key: `f-${f.id || f.createdAt}`,
          cells: [
            { key: 'project', value: f?.project?.projectName || '-' },
            { key: 'vehicle', value: f?.vehicle?.plateNumber || '-' },
            { key: 'requester', value: f?.user?.name || '-' },
            { key: 'status', value: f.status || '-' },
            { key: 'cost', value: this.formatCurrency(f.totalCost) },
          ],
        }));
      }

      return this.compliances.map((c) => ({
        _key: `c-${c.id || c.expiryDate}`,
        cells: [
          { key: 'vehicle', value: c?.vehicle?.plateNumber || '-' },
          { key: 'type', value: c?.type?.name || '-' },
          { key: 'expiry', value: this.formatDate(c.expiryDate) },
          { key: 'status', value: c.status || '-' },
        ],
      }));
    },
    vehicleStatusChartData() {
      try {
        const counts = {};
        (this.vehicles || []).forEach((v) => {
          const key = v?.status?.name || 'No Status';
          counts[key] = (counts[key] || 0) + 1;
        });
        const labels = Object.keys(counts);
        if (!labels.length) {
          return { labels: [], datasets: [] };
        }
        const colorMap = {
          AVAILABLE: 'rgba(16, 185, 129, 0.8)',
          ON_TRIP: 'rgba(59, 130, 246, 0.8)',
          MAINTENANCE: 'rgba(245, 158, 11, 0.8)',
          'No Status': 'rgba(156, 163, 175, 0.8)',
        };
        return {
          labels,
          datasets: [{
            data: labels.map((l) => counts[l]),
            backgroundColor: labels.map((l) => colorMap[l] || 'rgba(99, 102, 241, 0.7)'),
            borderWidth: 1,
          }],
        };
      } catch (e) {
        return { labels: [], datasets: [] };
      }
    },
    fuelStatusChartData() {
      try {
        const order = ['PENDING', 'APPROVED', 'ALLOCATED', 'DECLINED'];
        const counts = {};
        (this.fuels || []).forEach((f) => {
          const key = f.status || 'UNKNOWN';
          counts[key] = (counts[key] || 0) + 1;
        });
        const labels = [
          ...order.filter((s) => counts[s]),
          ...Object.keys(counts).filter((k) => !order.includes(k)),
        ];
        if (!labels.length) {
          return { labels: [], datasets: [] };
        }
        const colorMap = {
          PENDING: 'rgba(245, 158, 11, 0.8)',
          APPROVED: 'rgba(16, 185, 129, 0.8)',
          ALLOCATED: 'rgba(59, 130, 246, 0.8)',
          DECLINED: 'rgba(239, 68, 68, 0.8)',
        };
        return {
          labels,
          datasets: [{
            label: 'Fuel Requests',
            data: labels.map((l) => counts[l] || 0),
            backgroundColor: labels.map((l) => colorMap[l] || 'rgba(99, 102, 241, 0.7)'),
            borderRadius: 6,
          }],
        };
      } catch (e) {
        return { labels: [], datasets: [] };
      }
    },
    tripActivityChartData() {
      try {
        const now = new Date();
        const days = Array.from({ length: 7 }, (_, i) => {
          const d = new Date(now);
          d.setDate(now.getDate() - (6 - i));
          return d;
        });
        const labels = days.map((d) =>
          d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        );
        const counts = Array(7).fill(0);
        (this.trips || []).forEach((trip) => {
          if (!trip.startTime) return;
          const t = new Date(trip.startTime);
          for (let i = 0; i < 7; i++) {
            if (
              t.getFullYear() === days[i].getFullYear() &&
              t.getMonth() === days[i].getMonth() &&
              t.getDate() === days[i].getDate()
            ) {
              counts[i]++;
              break;
            }
          }
        });
        const hasData = counts.some((count) => count > 0);
        if (!hasData) {
          return { labels: [], datasets: [] };
        }
        return {
          labels,
          datasets: [{
            label: 'Trips',
            data: counts,
            backgroundColor: 'rgba(11, 110, 243, 0.7)',
            borderRadius: 6,
          }],
        };
      } catch (e) {
        return { labels: [], datasets: [] };
      }
    },
    complianceStatusChartData() {
      try {
        const counts = {};
        (this.compliances || []).forEach((c) => {
          const key = c.status || 'UNKNOWN';
          counts[key] = (counts[key] || 0) + 1;
        });
        const labels = Object.keys(counts);
        if (!labels.length) {
          return { labels: [], datasets: [] };
        }
        const colorMap = {
          VALID: 'rgba(16, 185, 129, 0.8)',
          EXPIRING_SOON: 'rgba(245, 158, 11, 0.8)',
          EXPIRED: 'rgba(239, 68, 68, 0.8)',
          UNKNOWN: 'rgba(156, 163, 175, 0.8)',
        };
        return {
          labels,
          datasets: [{
            data: labels.map((l) => counts[l]),
            backgroundColor: labels.map((l) => colorMap[l] || 'rgba(99, 102, 241, 0.7)'),
            borderWidth: 1,
          }],
        };
      } catch (e) {
        return { labels: [], datasets: [] };
      }
    },
    hasVehicleStatusData() {
      return Array.isArray(this.vehicleStatusChartData?.datasets) && this.vehicleStatusChartData.datasets.length > 0;
    },
    hasFuelStatusData() {
      return Array.isArray(this.fuelStatusChartData?.datasets) && this.fuelStatusChartData.datasets.length > 0;
    },
    hasTripActivityData() {
      return Array.isArray(this.tripActivityChartData?.datasets) && this.tripActivityChartData.datasets.length > 0;
    },
    hasComplianceStatusData() {
      return Array.isArray(this.complianceStatusChartData?.datasets) && this.complianceStatusChartData.datasets.length > 0;
    },
  },
  methods: {
    formatDate(v) {
      if (!v) return '-';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return '-';
      return d.toLocaleDateString();
    },
    formatNumber(v) {
      if (v === null || v === undefined || Number.isNaN(Number(v))) return '-';
      return new Intl.NumberFormat().format(Number(v));
    },
    formatCurrency(v) {
      if (v === null || v === undefined || Number.isNaN(Number(v))) return '-';
      return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(Number(v));
    },
    async loadFleetData() {
      this.loading = true;
      try {
        const payload = await fleetApi.getFleetDashboard();
        this.vehicles = Array.isArray(payload?.vehicles) ? payload.vehicles : [];
        this.drivers = Array.isArray(payload?.drivers) ? payload.drivers : [];
        this.trips = Array.isArray(payload?.trips) ? payload.trips : [];
        this.fuels = Array.isArray(payload?.fuels) ? payload.fuels : [];
        this.compliances = Array.isArray(payload?.compliances) ? payload.compliances : [];
        this.stats = {
          ...this.stats,
          ...(payload?.stats || {}),
        };
      } catch (err) {
        const message = err?.message || 'Failed to load fleet data';
        if (this.$toast?.error) this.$toast.error(message);
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    const qTab = this.$route?.query?.tab;
    if (qTab && this.tabs.includes(qTab)) {
      this.activeTab = qTab;
    }
    this.loadFleetData();
  },
  watch: {
    '$route.query.tab'(value) {
      if (value && this.tabs.includes(value)) {
        this.activeTab = value;
      }
    },
  },
};
</script>

<style scoped>
.fleet-page {
  --fleet-accent: #0b6ef3;
  --fleet-ink: #11243e;
  --fleet-muted: #58708f;
  --fleet-card: rgba(255, 255, 255, 0.9);
  min-height: calc(100vh - var(--app-header-height));
  padding: 22px;
  background: var(--fleet-bg);
  font-family: 'Cerebri Sans', 'Segoe UI', sans-serif;
  color: var(--fleet-ink);
}

.fleet-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.fleet-kicker {
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 700;
  color: var(--fleet-accent);
  font-size: 12px;
}

.fleet-hero h1 {
  margin: 6px 0;
  font-size: clamp(1.4rem, 2.4vw, 2rem);
  font-weight: 800;
}

.fleet-subtitle {
  margin: 0;
  color: var(--fleet-muted);
  max-width: 700px;
}

.refresh-btn {
  border: 0;
  border-radius: 12px;
  background: linear-gradient(135deg, #0b6ef3 0%, #084eb0 100%);
  color: #fff;
  font-weight: 700;
  padding: 10px 16px;
  box-shadow: 0 10px 24px rgba(11, 110, 243, 0.24);
}

.refresh-btn:disabled {
  opacity: 0.75;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}

.metric-card {
  background: var(--fleet-card);
  border: 1px solid var(--fleet-border);
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 10px 24px rgba(11, 24, 48, 0.06);
}

.metric-card p {
  margin: 0;
  color: var(--fleet-muted);
  font-size: 12px;
}

.metric-card h3 {
  margin: 6px 0 0;
  font-size: 1.6rem;
  font-weight: 800;
}

.metric-card.warning {
  border-color: rgba(255, 153, 0, 0.4);
  background: linear-gradient(145deg, rgba(255, 244, 224, 0.9), rgba(255, 255, 255, 0.92));
}

.fleet-panel {
  background: var(--fleet-card);
  border: 1px solid var(--fleet-border);
  border-radius: 16px;
  padding: 14px;
}

.module-links {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.module-link {
  text-decoration: none;
  border: 1px solid rgba(11, 110, 243, 0.2);
  border-radius: 12px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.88);
  color: #184673;
  font-weight: 700;
  text-align: center;
}

.module-link:hover {
  border-color: #0b6ef3;
  color: #0b6ef3;
}

.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.tab-btn {
  border: 1px solid #d5e3f7;
  background: #fff;
  color: #325072;
  border-radius: 10px;
  padding: 8px 12px;
  font-weight: 700;
  font-size: 13px;
}

.tab-btn.active {
  border-color: var(--fleet-accent);
  background: rgba(11, 110, 243, 0.09);
  color: var(--fleet-accent);
}

.table-wrap {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th,
td {
  text-align: left;
  padding: 11px 10px;
  border-bottom: 1px solid #e8eef8;
  font-size: 13px;
}

th {
  color: #254160;
  font-weight: 800;
  white-space: nowrap;
}

.empty-state {
  text-align: center;
  padding: 20px;
  color: var(--fleet-muted);
}

.charts-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.chart-card {
  background: var(--fleet-card);
  border: 1px solid var(--fleet-border);
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 10px 24px rgba(11, 24, 48, 0.06);
}

.chart-title {
  margin: 0 0 10px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b6b6b;
  letter-spacing: 0.06em;
}

.chart-body {
  position: relative;
  height: 200px;
}

.chart-empty {
  height: 100%;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--fleet-muted);
  font-weight: 600;
  font-size: 0.9rem;
  background: rgba(11, 110, 243, 0.03);
  border: 1px dashed rgba(11, 110, 243, 0.2);
  border-radius: 10px;
}

@media (max-width: 1100px) {
  .metric-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .module-links {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .charts-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .fleet-page {
    padding: 14px;
  }

  .fleet-hero {
    flex-direction: column;
  }

  .metric-grid {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }

  .module-links {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }

  .charts-grid {
    grid-template-columns: 1fr;
  }

  .refresh-btn {
    width: 100%;
  }
}
</style>
